import { MonitorSmartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui';
import { sessionTime } from './format';
import styles from './StaffSessions.module.css';
import type { StaffSession } from './types';

export function SessionList({
  sessions,
  loading,
  error,
  onRetry,
  onRevoke,
  revokingId,
}: {
  sessions: StaffSession[] | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  onRevoke: (session: StaffSession) => void;
  revokingId?: string;
}) {
  const { t } = useTranslation();

  if (loading)
    return <LoadingState label={t('staffSessions.loadingSessions')} />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (!sessions?.length)
    return (
      <EmptyState
        icon={<MonitorSmartphone size={22} />}
        title={t('staffSessions.noSessions')}
        body={t('staffSessions.noSessionsBody')}
      />
    );

  return (
    <div className={styles.list}>
      {sessions.map((session) => {
        const revoked = Boolean(session.revokedAt);
        return (
          <article className={styles.session} key={session.id}>
            <div className={styles.main}>
              <div className={styles.heading}>
                <span className={styles.device}>{session.device}</span>
                <span className={styles.badges}>
                  {session.current && (
                    <Badge tone="brand">{t('staffSessions.thisDevice')}</Badge>
                  )}
                  {revoked && (
                    <Badge tone="neutral">{t('staffSessions.signedOut')}</Badge>
                  )}
                </span>
              </div>
              <dl className={styles.details}>
                <dt>{t('staffSessions.ipAddress')}</dt>
                <dd>{session.ipMasked ?? t('common.notAvailable')}</dd>
                <dt>{t('staffSessions.signedIn')}</dt>
                <dd>{sessionTime(session.createdAt)}</dd>
                <dt>{t('staffSessions.lastActive')}</dt>
                <dd>{sessionTime(session.lastSeenAt)}</dd>
                <dt>{t('staffSessions.expires')}</dt>
                <dd>{sessionTime(session.expiresAt)}</dd>
              </dl>
            </div>
            <div className={styles.actions}>
              <Button
                size="sm"
                variant="danger"
                disabled={session.current || revoked}
                loading={revokingId === session.id}
                title={
                  session.current
                    ? t('staffSessions.currentSessionHint')
                    : undefined
                }
                onClick={() => onRevoke(session)}
              >
                {t('staffSessions.signOut')}
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
